<script lang="ts" setup>
import BasketIcon from '~/assets/icons/search/24/cart.svg'
import AppButton from '~/components/ui/buttons/AppButton.vue'
import AppInputCounter from '~/components/ui/form/formElements/AppInputCounter.vue'
import Tooltip from '~/components/ui/tooltips/Tooltip.vue'
import useCartStore from '~/shared/store/modules/cart'
import useAccountStore from '~/shared/store/modules/account'
import endpoints from '~/shared/config/api'
import customFetch from '~/composables/custom-fetch'
import getDateInfo from '~/composables/date-formatter'
import { useMonitorSize } from '~/composables/monitor-size'
import type {
  IAddToCart,
  ISearchProduct,
  ISearchProductWarehouse,
  IPartnerPositionOnAdd,
} from '~/shared/interfaces/search/products'
import type { IMonitorSize } from '~/shared/interfaces/composables'
import type { IPositionOnAdd } from '~/shared/interfaces/downloadPositions'
import type { ICartProduct } from '~/shared/interfaces/cart'

const props = defineProps<IAddToCart>()
const emits = defineEmits<{
  (e: 'buy-button-clicked-on-mobile'): void
}>()

const { cart } = endpoints

let sizes: IMonitorSize | null = useMonitorSize()

const cartStore = useCartStore()
const accountStore = useAccountStore()
const productIdForPreviewSearch = props.warehouse.id
  ? props.warehouse.productId + '_' + props.warehouse.id
  : props.warehouse.productId
const productCount = ref(1)
const addInProccess = ref(false)
const isBtnMobileAddToCart = ref(false)

const productCountInCart = computed(() => {
  return cartStore.getCartPreview[productIdForPreviewSearch]
})
const productMaxCountOnAdd = computed(
  () => props.warehouse.quantityWithReserve - (productCountInCart.value || 0)
)

function getProductDataForAddToCart(
  warehouse: ISearchProductWarehouse,
  product: ISearchProduct,
  count: number
) {
  const { type, productId, city, price, id: warehouseId, name } = warehouse
  const { article, brand, title } = product
  if (type == 'partner') {
    return {
      productId,
      count,
      citySlug: city.slug,
      article,
      brand: brand.name,
      pricelist: name
    }

  }
  return {
    productId,
    price,
    article,
    title,
    warehouseId,
    count,
    brandId: brand.id,
  }
}

function guestProductAdd(
  warehouse: ISearchProductWarehouse,
  product: ISearchProduct,
  count: number,
  productAndWarehouseId: string,
  productId: string
) {
  const {
    city,
    price,
    name,
    dateDelivery,
    quantityWithReserve,
    isMarkdown,
    quantity,
    isPromo,
    isUsed,
    id,
    type,
  } = warehouse
  const { article, brand, title } = product
  let dateDeliveryFormated = dateDelivery

  if (dateDelivery) {
    const { dateDay, monthName } = getDateInfo(dateDelivery)
    dateDeliveryFormated = `${dateDay} ${monthName}`
  }

  const cartProduct: ICartProduct = {
    article,
    brand,
    city,
    title,
    count,
    price,
    quantity: quantityWithReserve || quantity,
    productId: productId,
    id: productAndWarehouseId,
    warehouse: {
      name,
      deliveryBefore: dateDeliveryFormated,
      id: id,
    },
    comment: null,
    isAvailable: true,
    used: isUsed,
    promo: isPromo,
    markdown: isMarkdown,
    selected: true,
    type,
  }

  const savedProducts = localStorage.getItem('cartProducts')
  const savedProductsList = (
    JSON.parse(savedProducts ?? '[]') as ICartProduct[]
  ).filter((savedProduct) => savedProduct.id != productAndWarehouseId)

  savedProductsList.push(cartProduct)
  localStorage.setItem('cartProducts', JSON.stringify(savedProductsList))
  localStorage.setItem('cartPreview', JSON.stringify(cartStore.cartPreview))
}

async function addProductsToCart() {
  const { warehouse, product } = props
  const isGuest = accountStore.userRole == 'guest'
  const productCountNum = Number(productCount.value)
  const count = productCountInCart.value
    ? productCountNum + productCountInCart.value
    : productCountNum

  if (!isGuest) {
    addInProccess.value = true

    const dataOnAdd = getProductDataForAddToCart(warehouse, product, count)
    const reqConfig =
      warehouse.type == 'partner'
        ? cart.addToCartPartnerProduct([dataOnAdd as IPartnerPositionOnAdd])
        : cart.addToCart1CProduct([dataOnAdd as IPositionOnAdd])
    const { success } = await customFetch(reqConfig)

    addInProccess.value = false
    if (!success) {
      return alert('Что-то пошло не так!')
    }
  }

  cartStore.setCartPreviewData({
    key: productIdForPreviewSearch,
    count,
  })

  if (isGuest) {
    guestProductAdd(
      warehouse,
      product,
      count,
      productIdForPreviewSearch,
      props.warehouse.productId
    )
  }

  productCount.value = 1
}

function showAddToCartOnMobile() {
  emits('buy-button-clicked-on-mobile')
  isBtnMobileAddToCart.value = true
}

watch(() => props.isInputShown, (newValue)=> {
  isBtnMobileAddToCart.value = newValue
})
</script>

<template>
  <ClientOnly>
    <template v-if="!sizes.isMobile">
      <AppInputCounter
        v-model="productCount"
        :counter-settings="{
          count: 1,
          maxCount: productMaxCountOnAdd,
          isDisabled: !productMaxCountOnAdd || addInProccess,
          disabledOnlyForZeroMaxCount: true,
        }"
        class="add-to-cart-counter"
      />
      <button
        @click="addProductsToCart"
        tabindex="0"
        class="add-to-cart-btn"
        :disabled="!productCount || !productMaxCountOnAdd || addInProccess"
        :class="
          ((!productMaxCountOnAdd || addInProccess) && 'disabled') ||
          (!productCount && 'disabled')
        "
      >
        <span
          class="add-to-cart-btn__icon tooltip-container"
          :class="
            !productMaxCountOnAdd || addInProccess
              ? 'svg-disabled-fill-black'
              : 'svg-fill-orange'
          "
        >
          <BasketIcon />
          <Tooltip
            v-if="!warehouse.quantityWithReserve && warehouse.reserve"
            position="right"
          >
            <span
            >Весь товар зарезервирован, <br />
              обратитесь к менеджеру.</span
            >
          </Tooltip>
        </span>

        <span class="add-to-cart-btn__counter" v-if="productCountInCart">
          {{ productCountInCart }}
        </span>
      </button>
    </template>
    <template v-else>
      <AppButton
        v-if="!isBtnMobileAddToCart"
        class="add-to-cart-btn__mobile"
        type="black"
        text="Купить"
        size="s"
        @click="showAddToCartOnMobile"
      />
      <template v-else>
        <AppInputCounter
          class="add-to-cart-counter add-to-cart-counter_mobile"
          v-model="productCount"
          type="white"
          :counter-settings="{
            count: 1,
            maxCount: productMaxCountOnAdd,
            hasButtons: true,
            hasDebounce: true,
          }"
        />
        <AppButton
          class="add-to-cart-btn__mobile"
          :class="[addInProccess && 'pointer-events-none']"
          type="black"
          text="В корзину"
          size="s"
          :is-disabled="!productMaxCountOnAdd"
          @click="addProductsToCart"
        />
      </template>
    </template>
  </ClientOnly>
</template>

<style lang="scss" scoped>
.add-to-cart-counter {
  width: auto;

  &_mobile{
    :deep(.counter-btn),
    :deep(.counter-input) {
      height: 100%;
    }
  }
}
.add-to-cart-btn {
  position: relative;
  &.disabled {
    cursor: default;

    svg {
      :deep(path) {
        fill: $control-content_disabled;
      }
    }
  }

  &__mobile {
    flex-grow: 1;
  }

  &__icon {
    display: block;
    width: 24px;
    height: 24px;
  }

  &__counter {
    border-radius: 20px;
    border: 2px solid $control-content_white;
    background-color: $control-bg_primary;
    color: $control-content_white;
    padding: 0 4px;
    @include font(12px, 16px, 500);
    position: absolute;
    left: 10px;
    top: -4px;
    min-width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
  }

  .tooltip__txt {
    &:not(:first-child) {
      margin-top: 8px;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
  }
}
</style>
